// RPR-6770: which legacy sub-app a cached session is allowed to belong to.
//
// `myDetails` lives in sessionStorage, which is per-tab per-ORIGIN -- and every legacy sub-app
// (/admin/, /client/, /payer/, /login/, /document/) shares one origin. So a session cached by one
// sub-app is visible to all of them, and starting "access on behalf of" caches the *payer's*
// session while the admin is still on /admin/ (see impersonateByAdmin -> isLoggedIn below).
//
// Sub-apps not listed here serve every user type -- /login/ and /document/ are reachable by
// admins, clients and payers alike, and so is the top-level app at '/' -- so they impose no
// constraint. Keep it that way: adding one would break their shared use.
//
// Prototype-less on purpose. A plain literal would answer to Object.prototype's keys, so a path
// like '/constructor/' would look up a truthy function, never match any user type, and discard
// every session on every read -- an unbounded discard/refetch/report cycle from a single stray
// URL. This is the only validation gate in the change, so its failure mode should not be total.
const AH_SUB_APP_USER_TYPES = Object.assign(Object.create(null), {
  admin: 'admin',
  client: 'client',
  payer: 'payer'
});

// The three helpers below hang off globalThis rather than being plain declarations, and sit outside
// the service rather than inside it, so the Karma suite can exercise them directly -- there is no
// angular-mocks in this repo, so anything needing the injector is far more awkward to test. Same
// convention as globalThis.buildFusionActionMenu in services/fusion_action_menu.js; they are
// declared in .eslintrc.json's `globals` to match.

// Each sub-app is served from its own directory, so the first path segment names it. Tolerates a
// missing trailing slash ('/admin' as well as '/admin/index.html'); returns '' for the top-level
// app at '/', which has no sub-app of its own.
globalThis.ahSubAppFromPath = function (pathname) {
  const match = /^\/([^/]+)/.exec(pathname || '');
  return match ? match[1] : '';
};

/**
 * Whether a cached session's user type can belong to the sub-app at `pathname`.
 *
 * Only a user type that positively *disagrees* with the sub-app counts as a mismatch. A cached
 * session carrying no user_type_name is malformed rather than misplaced, and redirectLoggedIn
 * below has its own branch for that which must keep no-oping -- discarding it here instead would
 * turn that silent no-op into a refetch-and-redirect on a path shared portal-wide.
 */
globalThis.ahIsSessionForSubApp = function (userTypeName, pathname) {
  const required = AH_SUB_APP_USER_TYPES[ahSubAppFromPath(pathname)];
  if (!required || !userTypeName) {
    return true;
  }
  return userTypeName === required;
};

/**
 * Whether `pathname` is the admin or client sub-app, where a redirect to the new *payer* portal
 * can never be correct (RPR-6770).
 *
 * An admin or client user is never a new-portal payer, and impersonation always redirects to the
 * impersonated user's own portal -- it never leaves the admin sitting on /admin/. So a
 * `use_legacy_portal === false` reading on these two sub-apps can only have come from a session
 * that is not this page's, and acting on it sends the admin to the payer portal, which bounces
 * them straight back.
 *
 * Deliberately an allow-list of the two sub-apps rather than "not /payer/": /login/, /document/
 * and the top-level app all legitimately hand a new-portal payer off to the new portal.
 */
globalThis.ahIsAdminOrClientSubApp = function (pathname) {
  const subApp = ahSubAppFromPath(pathname);
  return subApp === 'admin' || subApp === 'client';
};

// Mirrors portal_helper.js's phReportWrongUserRetryProblem, including the Sentry guard: this runs
// on a plain cache read, and a blocked vendor bundle must not turn that into a ReferenceError.
// js/sentry.js is a bare Sentry.init() with no captureConsole integration, so the console.warn
// does not reach Sentry on its own -- hence both.
//
// Three rules the sibling does not follow, all learned from reviewing this one:
//   - `message` is a FIXED string and everything variable goes in `details`. Sentry groups by
//     message, so interpolating a JSON byte offset (which moves with each distinct corruption) or
//     a pathname would shatter one issue into hundreds.
//   - `error_` is passed through rather than concatenated, so its stack survives. Concatenating
//     stringifies it and throws the stack away, which is the whole value of having caught it.
//   - No ticket key in the runtime text. It would outlive RPR-6770 in the Sentry issue list; the
//     message describes the symptom instead. The `{extra: ...}` context form matches
//     app_common.js's captureException call.
//
// Wrapped in its own try/catch, unlike the sibling: that one runs inside an error handler, this
// one runs on the hot cache-read path, where a throw out of the vendor bundle would take an
// ordinary is_logged_in() digest down with it.
function ahReportStaleSession(message, details, error_) {
  console.warn('authHelper: ' + message, details, error_);
  try {
    if (typeof Sentry === 'undefined') {
      return;
    }
    if (error_ && typeof Sentry.captureException === 'function') {
      Sentry.captureException(error_, { extra: { reason: message, details: details } });
    } else if (typeof Sentry.captureMessage === 'function') {
      Sentry.captureMessage(message, { level: 'warning', extra: details });
    }
  } catch (reportingError) {
    console.warn('authHelper: could not report "' + message + '"', reportingError);
  }
}

app.service('authHelper', ['$window', 'storageService', 'apiHelper', '$http', '$location', 'CONSTANTS', 'SETTINGS',
  function ($window, storageService, apiHelper, $http, $location, CONSTANTS, SETTINGS) {

    const authHelper = this;
    // auth_params holds these parameters:
    // 'is_impersonation', 'is_authenticated', 'login'
    const shared_data = { 'me_orig': {}, 'me': {} };
    const LOGIN_PREFIX = 'login_';
    const LOGIN_PREFIX_ORIG = 'loginOrig_';
    const IMPERSONATION_KEY = 'is_impersonation';
    const IMPERSONATION_ACTIVE = 'active';
    const IMPERSONATION_INACTIVE = 'inactive';
    // Whether the logged-in user's details -- including the permission list they
    // carry -- have been retrieved in this page instance. shared_data always
    // starts empty, and a refresh usually drops the cached copy too
    // (common_app_controller.js binds removeStaleLoginDetails to 'unload', which
    // no longer fires in every case), so on every load there is a window in
    // which the current user and their permissions are not known. Permission
    // checks must not guess during that window (RPR-6816).
    let user_details_loaded = false;
    // Whether the retrieval has finished at all, successfully or not. Callers that
    // hold rendering back until the user is known need to stop waiting at some
    // point: isLoggedIn() deliberately leaves the page in place on an aborted
    // request and on a public route, so keying only off user_details_loaded would
    // leave those blank for good.
    let user_details_retrieval_settled = false;
    const IMPERSONATED_FROM_URL_KEY = 'impersonated_from_url';

    // Set once a session was found in the cache and refused, for ANY reason -- it belonged to
    // another sub-app, it would not parse, or it parsed to a payload with nothing usable in it.
    // Named for the refusal rather than for one cause of it: it used to be
    // `discardedForeignSession` and was set only on the sub-app mismatch, which left the parse and
    // parses-to-null exits returning null with this still false. That is not a cosmetic gap --
    // checkPortalVersionIfNeeded then reads those as "anonymous visitor" and redirects a signed-in
    // admin to the payer portal, which is RPR-6770 with the sub-apps swapped, in the very branch
    // this latch was added to close.
    //
    // Never un-set: it answers "is this browser signed in as somebody?", and having refused a
    // session is proof that it is.
    //
    // Scoped to the service rather than the module so its lifetime is one injector, i.e. one
    // document, i.e. one page load -- which is the scope the claim above is true for. That also
    // keeps specs isolated for free, since each angular.injector() builds a fresh authHelper.
    let refusedCachedSession = false;

    // One breadcrumb per page load, not per refusal. The read path cannot stop the write path
    // re-caching a session it will refuse again on the next read (see the note on the setItem in
    // isLoggedIn), so an unlatched report would fire once per refetch for as long as the tab is
    // open. Bounding it here is what makes that cycle visible without making it noise.
    let refusedSessionReported = false;

    // The exact string written by the one caller that deliberately caches a session for a sub-app
    // this document is NOT on: restoreSharedDataInLocalStorage, i.e. the legacy Finish Access widget
    // restoring the original ADMIN session while still on /payer/ or /client/, immediately before
    // navigating to /admin/. getCachedLoginDetails serves it back rather than refusing it.
    //
    // Keyed on the written string, not on a shape or a user type. That is the whole safety argument:
    // it can only ever match a value this document wrote a moment earlier through that one function,
    // so no leftover, forged or refetched value can satisfy it -- which is precisely what
    // `use_legacy_portal === false` failed to guarantee when it was used for the same purpose.
    let trustedRestoredSession = null;

    // Memoized on the exact string last read out of sessionStorage. is_logged_in() sits in an ng-if
    // on all three headers, so this accessor runs on every digest and digests loop until stable;
    // getUserPermissions() shares it, so permissionsHelper.hasPermission() would otherwise cost two
    // full JSON.parse calls per call, multiplied by list length in hasAnyOfPermissions -- and an
    // admin's myDetails carries permission arrays. Keyed on the raw string alone, so any write
    // invalidates it; $window.location.pathname is the other input and cannot change without a
    // document load, which is also the lifetime of this injector.
    let lastRawDetails = null;
    let lastCachedDetails = null;

    authHelper.getSharedData = function () {
      return shared_data;
    };

    authHelper.hasUserDetailsRetrievalSettled = function () {
      return user_details_retrieval_settled;
    };

    authHelper.getLoginPrefix = function () {
      return authHelper.isImpersonation() ? LOGIN_PREFIX : LOGIN_PREFIX_ORIG;
    };

    authHelper.getLoginPrefixOrig = function () {
      return LOGIN_PREFIX_ORIG;
    };

    authHelper.setLogin = function (login, use_orig_login) {

      // Serializes login to localStorage as string
      if (use_orig_login) {
        storageService.getLocalStorage()[LOGIN_PREFIX_ORIG] = JSON.stringify(login);
      } else {
        storageService.getLocalStorage()[authHelper.getLoginPrefix()] = JSON.stringify(login);
      }
    };

    // TODO: RPR-4034 Might need to remove this
    authHelper.getLogin = function (use_orig_login) {
      // Deserializes login from localStorage

      if (!storageService.getLocalStorage()) {
        return {};
      }

      const login = (use_orig_login)
        ? storageService.getLocalStorage()[LOGIN_PREFIX_ORIG]
        : storageService.getLocalStorage()[authHelper.getLoginPrefix()];

      if (!login) {
        return {};
      }

      return JSON.parse(login);
    };

    /**
     * The identity behind isUserAdmin/isUserPayer/isUserClient and getUserPermissions' non-empty
     * gate: "who is this browser signed in as".
     *
     * Deliberately NOT getCachedLoginDetails() alone. That answers a different question -- "may
     * THIS PAGE act on the cached session" -- which is about the sub-app, not about the user.
     * Routing identity through the sub-app gate made all three predicates false for an admin on
     * /payer/, which is a legitimate state, not a defect: all_faqs_view.js then builds an empty
     * promise list and dereferences result[0] of a resolved [], and
     * payer_expertise_form.component.js silently takes its non-admin branch on the authenticated
     * expertise-form link the backend itself builds (see checkPortalVersionIfNeeded).
     *
     * Preference order, each step load-bearing:
     *   1. shared_data['me'] -- what /api/mydetails/ said on THIS page load. Current, and
     *      sub-app-independent because the server answers about the user, not about the URL.
     *      Empty until isLoggedIn() lands, hence step 2.
     *   2. the sub-app-validated cache -- so a boot-time read still answers correctly in the
     *      ordinary case (a payer on /payer/) instead of reporting "nobody" until the first
     *      refetch. Not cosmetic: permissionsHelper.hasPermission returns TRUE whenever
     *      isUserAdmin() is false, so "nobody" fails OPEN and would flash permission-gated
     *      controls on every page boot.
     *   3. [] -- and never the raw cache, which is the one source that can name a FOREIGN user.
     *      A payer session left on /admin/ would make isUserPayer() true there, which is RPR-6770
     *      again with the predicates standing in for the redirect.
     *
     * [] rather than null when there is nothing usable: all four dependents rely on that shape.
     * getUserPermissions below length-checks it, and isUserAdmin/isUserPayer/isUserClient read
     * .user_type_name unguarded -- harmless on [], a TypeError on null.
     */
    const getMyDetails = function () {
      // Never read sessionStorage.myDetails here -- only getCachedLoginDetails, which is where
      // the ahIsSessionForSubApp gate lives. Step 3 of the docstring says why. Restored after a
      // conflict resolution put a raw JSON.parse of that key ahead of these two lines; do not
      // reintroduce one. A local try/catch is not needed either: an unparseable value goes
      // through ahValidateCachedSession, which drops the key, latches the refusal, reports it
      // once per page load and returns null -- which the `|| []` below turns into the [] shape
      // the four dependents expect.
      // RPR-6816 is served by step 2, not by a JSON.parse here. A corrupt or half-written
      // cache has to read as "unavailable" rather than throw -- this runs under
      // areUserDetailsLoaded(), which hasPermission() consults on every digest across ~45
      // admin templates, so an exception would abort rendering instead of denying a
      // permission. getCachedLoginDetails already catches that (ahValidateCachedSession),
      // reports it once, and returns null; `|| []` covers that and a stored literal null
      // alike. Parsing the raw string here instead would ALSO defeat step 3 -- see above.
      if (shared_data['me']?.user_type_name) {
        return shared_data['me'];
      }
      return authHelper.getCachedLoginDetails() || [];
    };

    authHelper.areUserDetailsLoaded = function () {
      // Deliberately a live check and not just the latch: the permission list is
      // read from shared_data.me while the cached copy is what survives a reload,
      // so a caller asking whether the user is known is really asking whether those
      // two describe the same user. They can come apart -- the cached copy is cleared
      // mid-session (logout, removeStaleLoginDetails), or a details payload
      // reaches shared_data while writing the cached copy throws and leaves an
      // earlier user in place, or -- most often, post-RPR-6770 -- the sub-app gate
      // refuses an intact current session. Either way an answer of "known" would
      // let permissionsHelper decide against two stores describing different
      // users, with no way to tell which is current. Mismatches fail closed
      // (RPR-6816). The specific fail-open that motivated the ticket -- a stale
      // payer in the cache making isUserAdmin() false and so taking the blanket
      // allow -- is now blocked by getMyDetails preferring shared_data.me as well;
      // this check is what holds if that preference order is ever changed back.
      //
      // Both ids have to be present, not merely equal: two absent ones compare
      // equal and would quietly reduce this to a user-type comparison. Every
      // /api/mydetails/ payload carries an id for all three user types, so
      // demanding one costs nothing here -- and if that ever stops being true,
      // denying is the right way to find out.
      if (!user_details_loaded) {
        return false;
      }

      // getCachedLoginDetails() and NOT getMyDetails(): whenever shared_data.me names a user
      // type, getMyDetails returns that very object, so the two equality clauses below would
      // compare it with itself and the whole check would collapse to "shared_data.me has a
      // user type and an id" -- a liveness check on one store rather than a comparison of two.
      // That is the loosening permission_gate.spec.js's header warns about and a silent return
      // to the RPR-6816 fail-open, so this side has to be the store that is genuinely separate
      // from shared_data. Worse than it sounds: because setMeShared only ever merges keys,
      // user_details_loaded implies shared_data.me has a user type, so getMyDetails' cache
      // fallback is unreachable from here and the comparison would be vacuous every time.
      //
      // The docstring on getMyDetails argues the opposite way for identity, and both hold:
      // that one answers "who is this browser signed in as", where the sub-app gate is the
      // wrong question, while this one answers "do the two stores agree", where it belongs.
      // The cost is real but currently unreachable -- an admin on /payer/ or /client/ has
      // their cached session refused there, so readiness stays false for the life of the page
      // and hasPermission() denies everything. Safe only because permissionsHelper is the
      // sole consumer and no /payer/ or /client/ markup is permission-gated; check that still
      // holds before adding a permission-gated control to one (pinned below in
      // permission_gate.spec.js).

      // The optional chaining on shared_me is defensive, not a reaction to any known path,
      // and the distinction matters: the *cached* copy above genuinely is cleared
      // mid-session (removeStaleLoginDetails, see the comment at the top of this function),
      // but shared_data.me is not. It is a fixed key on this service's private object and
      // setMeShared mutates its keys rather than reassigning it, so it is always an object
      // today -- do not "fix" removeStaleLoginDetails to clear it as well, because the
      // cache-clears / shared-record-persists asymmetry is exactly what this check reads.
      // The `?.` costs nothing and keeps this failing closed if a caller ever starts writing
      // through the reference getSharedData() hands out.
      // getCachedLoginDetails, NOT getMyDetails: this is a cross-check of the two stores
      // against each other, and getMyDetails now PREFERS shared_data.me (RPR-6770, step 1 of
      // its preference order). Sourcing the left-hand side from it would compare shared_data
      // with itself, so the two could never be seen to disagree -- the mismatch would read as
      // "known" and hand an admin's permission list out against a stale payer's user type,
      // which is the RPR-6816 fail-open this whole function exists to close. `|| []` because
      // a cleared, unparseable or refused cache gives null and the reads below must not throw:
      // hasPermission() calls this on every digest across ~45 gated admin templates.
      const cached_details = authHelper.getCachedLoginDetails() || [];
      const shared_me = shared_data.me;
      return !!cached_details.user_type_name
        && cached_details.id != null
        && shared_me?.id != null
        && cached_details.user_type_name === shared_me.user_type_name
        && cached_details.id === shared_me.id;
    };

    authHelper.isUserAdmin = function (use_orig_login) {
      const myDetails = getMyDetails();
      return myDetails.user_type_name === 'admin';
    };

    authHelper.isUserPayer = function (use_orig_login) {
      const myDetails = getMyDetails();
      return myDetails.user_type_name === 'payer';
    };

    authHelper.isUserClient = function (use_orig_login) {
      const myDetails = getMyDetails();
      return myDetails.user_type_name === 'client';
    };

    authHelper.isLoginPage = function () {
      return ($window.location.pathname === '/login/');
    };

    /**
     * Whether this page is in the admin or client sub-app, where a redirect to the new *payer*
     * portal can never be correct (RPR-6770). Exposed on the service because portal_helper's
     * portalReady needs the same gate; see ahIsAdminOrClientSubApp for why the rule holds.
     */
    authHelper.isAdminOrClientSubApp = function () {
      return ahIsAdminOrClientSubApp($window.location.pathname);
    };

    // isCorrectPortalInPath() removed -- no callers; the sub-app rule now lives in
    // ahIsSessionForSubApp.

    authHelper.isPublicRoute = function () {
      const publicRoutes = [
        '/Help',
        '/FAQs',
        '/PayerExpertiseForm'
      ];

      for (let i = 0; i < publicRoutes.length; i++) {
        if ($location.path().indexOf(publicRoutes[i]) == 0) {
          return true;
        }
      }
    };

    /**
     * Drops the per-session DataTables preferences (column visibility / sort order) written by
     * dataTablesUtils.get_session_state_options(). Call this wherever the session identity
     * changes, so the next user in this tab starts from default columns and sort rather than
     * inheriting the previous one's layout.
     */
    authHelper.clearTablePreferences = function () {
      Object.keys(sessionStorage)
        .filter(function (key) { return key.startsWith('DataTables_'); })
        .forEach(function (key) { sessionStorage.removeItem(key); });
    };

    const redirectToBackendLoginPage = function () {
      authHelper.clearTablePreferences();
      const frontendReturnURL = $location.absUrl();
      window.location.href = apiHelper.getApiUrl() +
      '/auth/login/?next=' + encodeURIComponent(frontendReturnURL);
    };

    authHelper.removeStaleLoginDetails = function () {
      sessionStorage.removeItem(CONSTANTS.LOGIN_DETAILS_KEY);
    };

    /**
     * Retires this origin's impersonation markers. One caller: an admin session arriving at
     * isLoggedIn(), i.e. a refetch has just CONFIRMED there is no live impersonation in this
     * browser.
     *
     * getCachedLoginDetails deliberately does not call this, and the history is worth keeping.
     * It used to, narrowed to `use_legacy_portal === false` on the theory that a new-portal payer
     * is the only session that can arrive from another origin, so anything else must be a
     * deliberate in-flight switch. The theory inverted the test it needed. A new-portal payer is
     * exactly what impersonateByAdmin caches -- only PayerMyDetailsSerializer exposes the field, and
     * it is false for precisely those payers -- so the narrowing excluded LEGACY payers and let the
     * mainstream path straight through. Starting access on behalf of a new-portal payer would then
     * retire the markers one tick after portal_helper.js wrote them, while the impersonation was
     * live: killing the `impersonated_from_url` fallback that finish_impersonation_widget still
     * reads, flipping `is_impersonation` to 'inactive' so getLoginPrefix() returns the ORIG slot,
     * and reporting a stale session on every single impersonation start.
     *
     * A cache read cannot tell "left over from a completed round trip" from "written a moment ago
     * for a switch in progress" by inspecting the session itself. Only the impersonation marker
     * distinguishes them, and only isLoggedIn() has the refetch that makes it authoritative -- which
     * is why the cleanup lives here and the read path is read-only.
     */
    authHelper.clearImpersonationLeftovers = function () {
      // getLocalStorage() falls back to an in-memory object when localStorage is unavailable, so
      // *obtaining* it never throws -- go through it rather than the global for that reason. It is
      // not a guarantee the write succeeds: isLocalStorageSupported() only probes for getItem, so
      // a present-but-quota-exhausted store still throws on assignment below. Hence the catch.
      //
      // Best-effort deliberately. getCachedLoginDetails() calls this mid-discard, on a path
      // $scope.is_logged_in() reaches every digest, and the very next statement there is the
      // ahReportStaleSession breadcrumb -- so letting a restricted store throw would drop the one
      // diagnostic this change exists to add, in exactly the browser configuration most likely to
      // need it. By then the discard itself is already complete (the cache key is removed and the
      // foreign-session latch set before this runs), so swallowing the write loses nothing else.
      //
      // `delete` rather than removeItem() to match storage.js's own clearLocalStorage(), which
      // deletes by key off this same object. Storage defines a named-property deleter, so the two
      // are equivalent on a real store, and only `delete` also works on the in-memory fallback.
      const store = storageService.getLocalStorage();
      // Two independent try blocks rather than one, so a store that refuses the first mutation still
      // gets the second. Sharing a block leaves `is_impersonation` stuck at 'active' AND
      // `impersonated_from_url` in place, which is the worst of the three outcomes and was also
      // indistinguishable from the others in the log.
      try {
        store[IMPERSONATION_KEY] = IMPERSONATION_INACTIVE;
      } catch (error_) {
        ahReportStaleSession('could not retire the impersonation marker', {
          key: IMPERSONATION_KEY, subApp: ahSubAppFromPath($window.location.pathname)
        }, error_);
      }
      try {
        delete store[IMPERSONATED_FROM_URL_KEY];
      } catch (error_) {
        ahReportStaleSession('could not retire the impersonated-from URL', {
          key: IMPERSONATED_FROM_URL_KEY, subApp: ahSubAppFromPath($window.location.pathname)
        }, error_);
      }
    };

    // Suppressed while impersonation is live, and that is the whole of the discriminator.
    //
    // A session for another sub-app is expected in exactly one state: a deliberate, in-flight
    // switch, where the cache holds the session we are about to navigate TO rather than one left
    // over from a completed trip. impersonateByAdmin is that state -- setImpersonationData() sets
    // the marker ACTIVE and only then does isLoggedIn() cache the impersonation target, while still
    // on /admin/. So isImpersonation() is true for precisely the in-flight case and false for the
    // leftover case.
    //
    // This is the check that `use_legacy_portal === false` was standing in for, and getting wrong:
    // a new-portal payer is exactly what impersonateByAdmin caches, so that test excluded only
    // LEGACY payers and let the mainstream path through. See clearImpersonationLeftovers.
    //
    // Not suppressed for restoreSharedDataInLocalStorage, which sets the marker INACTIVE before
    // caching the admin on /payer/: that write really is refused on the next read, and really does
    // cost a refetch, so one breadcrumb per Finish Access is a true report rather than noise.
    function ahReportRefusedSession(userTypeName) {
      if (refusedSessionReported || authHelper.isImpersonation()) {
        return;
      }
      refusedSessionReported = true;
      ahReportStaleSession('discarded a cached session that cannot belong to this sub-app', {
        cachedUserType: userTypeName || '(none)',
        subApp: ahSubAppFromPath($window.location.pathname)
      });
    }

    /**
     * The two refusals that are CORRUPTION rather than misplacement: a value that will not parse,
     * and one that parses to nothing usable. Shares refusedSessionReported with the sibling above,
     * so the page-load bound covers every breadcrumb rather than just one of them.
     *
     * No isImpersonation() suppression, unlike the sibling. That suppression exists because a
     * sub-app mismatch is the EXPECTED state during a deliberate in-flight session switch; a cache
     * that will not parse is never expected, so it is worth a breadcrumb mid-impersonation too.
     */
    function ahReportCorruptSession(message, myDetailsStr, error_) {
      if (refusedSessionReported) {
        return;
      }
      refusedSessionReported = true;
      ahReportStaleSession(message, {
        // Length plus a short prefix, never the value: myDetails carries names and emails, but the
        // leading `{"id":`-shaped bytes are exactly what tells truncation, a wrong-format write and
        // a clobber by another app on this shared origin apart. Carrying neither -- as the parse
        // branch used to -- leaves all three indistinguishable forever, and the offending string is
        // deleted in the same breath.
        rawLength: myDetailsStr.length,
        rawPrefix: myDetailsStr.slice(0, 16),
        subApp: ahSubAppFromPath($window.location.pathname)
      }, error_);
    }

    // The body of getCachedLoginDetails, split out so the memo above reads as a memo. Every `return
    // null` here is a refusal that must latch, EXCEPT the empty-cache one -- that is the only null
    // that really means "anonymous visitor".
    function ahValidateCachedSession(myDetailsStr) {
      if (!myDetailsStr) {
        return null;
      }
      let myDetails;
      try {
        myDetails = JSON.parse(myDetailsStr);
      } catch (error_) {
        // Drop an unparseable value rather than rethrowing: this is now the only reader, so
        // leaving it in place would throw here on every call for the rest of the tab's session.
        authHelper.removeStaleLoginDetails();
        refusedCachedSession = true;
        ahReportCorruptSession('discarded an unparseable cached session', myDetailsStr, error_);
        return null;
      }
      const userTypeName = myDetails?.user_type_name;
      // The trusted-write escape hatch is checked here, AFTER the parse, so a trusted value that has
      // somehow been corrupted still takes the unparseable path above rather than being waved
      // through. See trustedRestoredSession for why matching on the written string is safe where
      // matching on the session's own contents was not.
      if (myDetailsStr === trustedRestoredSession
          || ahIsSessionForSubApp(userTypeName, $window.location.pathname)) {
        if (!myDetails) {
          // A cache holding the literal string "null" (or any falsy payload) parses without
          // throwing, and then passes the sub-app check, because ahIsSessionForSubApp treats an
          // absent user type as malformed-rather-than-misplaced and returns true. So it reaches
          // `return myDetails` and hands back null -- indistinguishable from an empty cache unless
          // the latch is set here. Deliberately no removeStaleLoginDetails(): the docstring on
          // ahIsSessionForSubApp explains why a payload with no user type must stay put.
          //
          // Reported, and this is the exit that most needs it. Because the value stays put, the memo
          // above serves the refusal back on every subsequent read for the life of the tab, so the
          // symptom is permanent rather than self-healing: is_logged_in() gates the whole container
          // in all three headers, so the header disappears; hasSession() stays true on the latch, so
          // the idle watcher never logs the user out; and checkPortalVersionIfNeeded takes the
          // refusedCachedSession() early return. Reachable without any corruption -- an empty-body
          // 200 from /api/mydetails/ (proxy interstitial, CDN error page, gateway rewrite) gives
          // AngularJS `data === ''`, which isLoggedIn stores as '""' and parses back to a falsy
          // payload. Silent here meant that page arrived in support with nothing in Sentry.
          refusedCachedSession = true;
          ahReportCorruptSession('discarded a cached session that parsed to nothing usable',
            myDetailsStr);
          return null;
        }
        return myDetails;
      }
      authHelper.removeStaleLoginDetails();
      refusedCachedSession = true;
      ahReportRefusedSession(userTypeName);
      return null;
    }

    /**
     * The session cached for this tab, or null when there is none -- or when the one present
     * cannot belong to the sub-app we are in.
     *
     * That last case is RPR-6770. Starting "access on behalf of" caches the *payer's* session on
     * the legacy origin (isLoggedIn below) and then navigates to the new payer portal on a
     * different origin. That portal ends impersonation and sends the admin back here, but it
     * cannot clean this origin's storage. The only cleanup is the `unload` listener in
     * common_app_controller.js, and `unload` is deprecated in Chromium and never fires for a page
     * entering the back/forward cache -- so whether the cache survives depends on whether that
     * listener wins, which is a matter of timing rather than of browser identity. Deliberately NOT
     * stated as "Edge does X and Chrome does Y": both are Chromium and share these semantics, and
     * a measured Chromium run had the cleanup win. QA saw it only in Edge; nothing here explains
     * why, and guessing at per-browser bfcache heuristics is the wrong mental model. This fix does
     * not depend on the answer -- it removes the dependency on that listener entirely.
     *
     * A payer session left on an /admin/ page is not merely stale, it is actively harmful:
     * portalReady reads `use_legacy_portal === false` from it and navigates the admin page to the
     * new payer portal, whose auth guard sees an admin and bounces them straight back -- the
     * visible round trip QA reported.
     *
     * On permissions, two claims have now been wrong here and both are worth recording, because the
     * second was a correction that overshot. It does NOT hand the payer's permissions to an admin
     * screen: getUserPermissions returns shared_data['me'].permissions, using getMyDetails only as a
     * non-empty gate, and setMeShared MERGES keys, so payer details -- which carry no `permissions`
     * at all -- cannot displace an admin's. But nor does it hide admin controls, which is what this
     * comment claimed next. permissionsHelper.hasPermission short-circuits to TRUE whenever
     * isUserAdmin() is false, so a session the predicates cannot identify fails OPEN, not closed,
     * across all 32 permission-gated call sites via $rootScope.has_permission. That is pre-existing
     * on /admin/ -- a cached payer already made isUserAdmin() false there on develop -- and
     * getMyDetails now prefers shared_data['me'], so this change does not widen it. Do not
     * "simplify" that preference order without re-reading this paragraph.
     *
     * EVERY read of the cache goes through here rather than touching sessionStorage directly.
     * The readers have no defined ordering between them, so validating at the point of use is the
     * only placement with no race left in it: a boot-time hook would still be beaten by whichever
     * of them runs first.
     *
     * Read-only by design: it validates and reports, and it does NOT retire impersonation markers.
     * It used to, narrowed to `use_legacy_portal === false`, and that narrowing was wrong in the
     * mainstream case -- see the note on clearImpersonationLeftovers for what went wrong and why
     * isImpersonation() is the discriminator that actually holds.
     *
     * DO NOT MUTATE the returned object. It is memoized on the stored string, so it is shared by
     * every reader on this page load rather than being a private JSON.parse per call. To change a
     * cached session, write a copy back to sessionStorage -- which also invalidates the memo, since
     * the stored string is its key. checkPortalVersionIfNeeded in portal_helper.js is the one caller
     * that needs this and shows the shape.
     */
    authHelper.getCachedLoginDetails = function () {
      const myDetailsStr = sessionStorage.getItem(CONSTANTS.LOGIN_DETAILS_KEY);
      if (myDetailsStr === lastRawDetails) {
        return lastCachedDetails;
      }
      lastRawDetails = myDetailsStr;
      lastCachedDetails = ahValidateCachedSession(myDetailsStr);
      return lastCachedDetails;
    };

    /**
     * Whether a session was found in the cache and refused on this page load -- refused for ANY
     * reason, not just for belonging to another sub-app. getCachedLoginDetails has four null exits
     * and three of them are refusals: another sub-app's session, an unparseable value, and a value
     * that parses to nothing usable. Only the empty cache is not.
     *
     * Callers that branch on "getCachedLoginDetails() returned null" need this, because null
     * conflates two states that must not be conflated: nothing was cached (an anonymous visitor)
     * and something was cached and refused (definitely NOT anonymous). See
     * checkPortalVersionIfNeeded in portal_helper.js, where treating the second as the first sends
     * a signed-in admin to the new payer portal -- RPR-6770 in mirror image.
     */
    authHelper.refusedCachedSession = function () {
      return refusedCachedSession;
    };

    /**
     * Whether this browser has a session at all -- as distinct from whether it has one that is
     * usable on THIS sub-app, which is what getCachedLoginDetails answers.
     *
     * These came apart when getCachedLoginDetails started refusing sessions. Callers asking "is
     * anybody signed in?" must use this one; callers asking "whose session should drive this page?"
     * must use getCachedLoginDetails. Reading the second as the first is how a refused session turns
     * into a logged-out user, and the consequences are not uniform:
     *   - confirm_on_exit.js drops the unsaved-changes prompt AND tears down its own guard, so a
     *     dirty form is lost silently, with nothing logged;
     *   - the idle watcher in common_app_controller.js calls doLogoutAll();
     *   - help.component.js posts feedback down the unauthenticated path and takes a 403.
     * All three go through persistentService.isUserLoggedIn or this service directly, and all three
     * are reachable for as long as the refused session keeps being re-cached -- persistently, not
     * as a one-load blip.
     *
     * One further caller of persistentService.isUserLoggedIn inherits this answer without needing
     * it: login_dashboard.js's $scope.inactiveLogout, which asks whether to show "you were logged
     * out for inactivity". Considered and left alone. /login/ has no required user type, so
     * getCachedLoginDetails never refuses a session there for belonging to another sub-app, and the
     * only input on which the two answers differ is a cached value too corrupt to parse -- where
     * "somebody is signed in" is at least as defensible as "nobody is". An earlier revision pinned
     * that screen to the old question explicitly; it was reverted because editing the controller's
     * 11-argument signature made SonarCloud attribute its pre-existing too-many-parameters finding
     * to this PR, which is a poor trade for an unobservable difference.
     */
    authHelper.hasSession = function () {
      // getCachedLoginDetails() first, and not short-circuited away: it is what sets the latch on
      // this page load, so asking the latch before asking it would read a stale false.
      const cachedDetails = authHelper.getCachedLoginDetails();
      return !!cachedDetails || refusedCachedSession;
    };

    authHelper.isLoggedIn = function () {
      const url = apiHelper.getApiUrl() + '/api/mydetails/';
      return $http.get(url).then(
        function (result) {
          const myDetails = result.data;
          // Record who the user is before touching storage. Both writes below can
          // throw -- session storage on a quota/partitioning error, local storage
          // when it is unavailable (getLogin guards for exactly that) -- and a
          // throw here rejects into no handler at all, because the sibling error
          // handler of a .then(ok, err) pair only sees upstream rejections. Doing
          // this first means a storage fault degrades the page instead of leaving
          // callers that wait on the user (RPR-6816) waiting for good.
          authHelper.setMeShared(myDetails);
          // An admin session means impersonation cannot be active in this browser
          // (impersonation switches the session user to a payer/client). Clear
          // leftovers from a cross-origin round trip through the new payer portal,
          // which cleans its own origin's storage but not this one (RPR-6770).
          if (myDetails.user_type_name === 'admin' && authHelper.isImpersonation()) {
            authHelper.clearImpersonationLeftovers();
          }
          // Written unconditionally, on purpose: validating here instead would fire during
          // impersonateByAdmin, which legitimately caches the impersonation target while still on
          // /admin/. The cost of that decision is a cycle -- for a session that does not match this
          // sub-app, every read refuses it and every refusal is followed by another refetch and
          // another write, for as long as the tab is open. An admin who opens a /payer/ URL sits in
          // it indefinitely, where develop served that read from cache.
          //
          // So it is a decision, not an accident: the breadcrumb below is what makes it visible
          // (once per page load -- see refusedSessionReported), and auth_helper.spec.js pins the
          // request count for two consecutive reads so nobody "fixes" the cycle by accident either.
          sessionStorage.setItem(CONSTANTS.LOGIN_DETAILS_KEY, JSON.stringify(myDetails));
          if (!ahIsSessionForSubApp(myDetails.user_type_name, $window.location.pathname)) {
            ahReportRefusedSession(myDetails.user_type_name);
          }
          // no need to setMeSharedOrig because above endpoint will always return details
          // about current user only
          sessionStorage.setItem(CONSTANTS.LOGIN_DETAILS_KEY, JSON.stringify(myDetails));
          authHelper.setLogin(result.data, false);
          return result;
        },
        function (result) {
          if (!authHelper.isPublicRoute()) {
            // firefox aborts the current requests in case we change the page
            // which result in logout
            if ('abort' != result.xhrStatus) {
              redirectToBackendLoginPage();
            }
          }
          throw result;
        }
      ).catch(function (error_) {
        // Catches what the handler pair above cannot: a throw from the success
        // handler itself. Marking the retrieval settled here too means no
        // rejection, whatever its origin, can leave a gated view closed for good.
        // Rethrown unchanged so callers keep the rejection they expect.
        //
        // This is the only place that sees the permission authority failing outright,
        // and every downstream handler is silent: appController's is an empty stub and
        // authCheckController only branches on 'abort'. Without a line here a 500 from
        // /api/mydetails/ either bounces an admin to the login page with no explanation
        // or -- on a public route or an aborted request -- renders a screen with every
        // permission denied and no indication anything broke.
        //
        // Console only, no Sentry event: this path also takes every request Firefox aborts
        // on navigation away, which is routine browser behaviour rather than an incident,
        // and reporting it would bury real outages in noise.
        // eslint-disable-next-line no-console
        console.error('authHelper.isLoggedIn: user details retrieval failed', error_);
        user_details_retrieval_settled = true;
        throw error_;
      });
    };

    authHelper.getUserType = function () {
      const login = authHelper.getLogin(true);
      if (login) {
        return login['user_type_name'];
      }
    };

    authHelper.getUserTypeName = function () {
      const myDetails = authHelper.getCachedLoginDetails();
      if (myDetails) {
        const userTypeName = myDetails.user_type_name;

        return new Promise(function(resolve, reject) {
          if (userTypeName) {
            resolve({'data': {'user_type_name': userTypeName}});
          }
          else {
            reject('Data not found for user type name');
          }
        });
      }
      else {
        return authHelper.isLoggedIn();
      }
    };

    authHelper.redirectLoggedIn = function (returnUrl, isUrlNormal) {
      let angular_path = '';

      if (returnUrl) {
        if (isUrlNormal) {
          angular_path = '#' + returnUrl;
        } else {
          angular_path = '#' + decodeURIComponent(returnUrl);
        }
      }

      authHelper.getLoginDetails().then(
        function (myDetails) {
          const userType = myDetails?.user_type_name;
          if (!userType) {
            // Malformed cached session (no user_type_name) — match the prior
            // getUserTypeName() behaviour of silently no-oping rather than
            // redirecting to a broken '../undefined/' URL.
            return;
          }
          // New-portal payers hitting the old portal's top-level / login sub-app
          // (e.g. via an admin-notification email whose footer "Log in to RPR"
          // points to rpr.<domain>/) should be handed off to the new Angular
          // portal instead of being bounced to the legacy /payer/ sub-app.
          //
          // Never from /admin/ or /client/ though (RPR-6770): a `payer` reading on either of
          // those can only have come from a session that is not this page's, and acting on it
          // sends the admin to the payer portal, which bounces them straight back. This is a
          // second line of defence -- getCachedLoginDetails() should already have discarded such
          // a session -- and it is cheap, so keep both. The '../<userType>/' fallthrough below is
          // unchanged, which matters because showErrorCommon's wrong-user path funnels here from
          // a very large number of call sites (portal_helper.js's phIsWrongUserResponse carries the
          // count; deliberately not repeated here, so there is only one copy to go stale).
          if (userType === 'payer'
              && myDetails.use_legacy_portal === false
              && SETTINGS.NEW_PORTAL_URL
              && !authHelper.isAdminOrClientSubApp()) {
            const rawPath = returnUrl ? decodeURIComponent(returnUrl).replace(/^\//, '') : '';
            $window.location.href = rawPath
              ? SETTINGS.NEW_PORTAL_URL + '/' + rawPath
              : SETTINGS.NEW_PORTAL_URL;
            return;
          }
          $window.location.href = '../' + userType + '/' + angular_path;
        },
        function (error_) {
          // Without this handler a rejection was an unhandled promise rejection AND no
          // navigation at all -- a silent dead end. That matters most on the RPR-6770
          // path: portal_helper's wrong-user fallback routes every non-retryable
          // rejection through here, and a wrong-user 403 is exactly when
          // getLoginDetails() (which JSON.parses a sessionStorage value and otherwise
          // defers to isLoggedIn()) is most likely to reject.
          //
          // Log only, deliberately: making this navigate on rejection is a behaviour
          // change on a path shared portal-wide, so it is tracked in RPR-6888.
          console.warn('authHelper.redirectLoggedIn: could not resolve login details', error_);
        }
      );
    };

    authHelper.getMeSharedOrig = function () {
      return shared_data['me_orig'];
    };

    authHelper.getMeShared = function () {
      return shared_data['me'];
    };

    authHelper.getUserPermissions = function () {
      // roles/permissions are only defined on admin portal, so UI is adjusted according to user roles/permissions on
      // admin portal only.
      //
      // Returns [] both for a user who holds no permissions and for a user whose
      // details have not arrived yet -- the two are indistinguishable here, so
      // callers have to disambiguate (see areUserDetailsLoaded).
      // admin portal only

      // some times this code fires before we have login details,
      // often on page refresh because we clear the session storage
      // and due to this some times, some button/links which should be visible
      // get visible for fraction of second
      // to avoid this if details length is not there so return false
      // in case login details are not available, getMyDetails return empty array []
      //
      // Still reachable, and worth saying so because it briefly was not. The only production caller
      // is permissionsHelper.hasPermission, which reaches this line only AFTER isUserAdmin()
      // returned true -- so while getMyDetails was sourced purely from the sub-app-validated cache,
      // a true predicate implied a non-empty payload and this guard could never fire. getMyDetails
      // now falls back to that cache only when shared_data['me'] is still empty, which is exactly
      // the pre-login window this guard was written for, so the race it describes is live again.
      const myDetails = getMyDetails();
      if (Array.isArray(myDetails) && myDetails.length === 0) {
        return [];
      }

      const permissions = shared_data['me'].permissions;
      if (!permissions) {
        return [];
      }
      return permissions;
    };

    authHelper.setMeSharedOrig = function (data) {
      for (const k in data) {
        shared_data.me_orig[k] = data[k];
      }
    };

    authHelper.setMeShared = function (data, use_orig_login) {
      for (const k in data) {
        shared_data.me[k] = data[k];
      }

      // Every path that populates shared_data.me from a user-details payload --
      // a fresh /api/mydetails/ response or the cached copy replayed by
      // getLoginDetails() -- comes through here, and every such payload names the
      // user type and carries a 'permissions' list (only consulted for admins,
      // see permissionsHelper.hasPermission). Callers also pass unrelated
      // fragments through this setter, so the user_type_name check is what keeps
      // those from marking the user as known. Writing to the object handed out by
      // getSharedData() would bypass the flag; nothing does that today.
      if (data?.user_type_name) {
        user_details_loaded = true;
        user_details_retrieval_settled = true;
      }
    };

    authHelper.isImpersonation = function () {
      return storageService.getLocalStorage()[IMPERSONATION_KEY] === IMPERSONATION_ACTIVE;
    };

    authHelper.setImpersonationData = function () {
      storageService.getLocalStorage()[IMPERSONATION_KEY] = IMPERSONATION_ACTIVE;
    };

    /**
     * Ends impersonation on THIS origin: retires the marker and puts the original user's session
     * back in the cache. Called by the legacy Finish Access widget while still on /payer/ or
     * /client/, which then navigates to /admin/.
     *
     * The session it writes therefore belongs to a sub-app this document is not on, and
     * getCachedLoginDetails would refuse it -- remove the key, latch a refusal and report one --
     * about a digest later, because the headers' is_logged_in() runs on every digest. That cost more
     * than a wasted write: the very next thing the widget does is redirectLoggedIn(), whose
     * getLoginDetails() would then find an empty cache and fall through to isLoggedIn(). One extra
     * /api/mydetails/, issued while still on /payer/ -- and on a transient failure isLoggedIn()'s
     * rejection handler calls redirectToBackendLoginPage(), because a /payer/#/... URL is not in
     * isPublicRoute(). A blip at the exact moment an admin ends impersonation would drop them on the
     * Django login page, with `next` pointing at a payer URL they have no business on. On develop
     * this read was served from cache and made no request at all.
     *
     * Hence trustedRestoredSession: the accessor serves this one written string back instead of
     * refusing it, so redirectLoggedIn resolves from cache exactly as it did before. Note the
     * destination is unaffected either way -- appController calls isLoggedIn() on every page load,
     * so /admin/ refetches when it gets there regardless. What this removes is the request on the
     * page being LEFT, which is the one with the harmful failure mode.
     */
    authHelper.restoreSharedDataInLocalStorage = function () {
      authHelper.clearTablePreferences();
      storageService.getLocalStorage()[IMPERSONATION_KEY] = IMPERSONATION_INACTIVE;
      // restore me
      const IS_ORIGINAL_USER = true;
      const origUserDetails = authHelper.getLogin(IS_ORIGINAL_USER);
      // Was setMeShared(authHelper.getLogin(origUserDetails)), which passed the details object as
      // getLogin's `use_orig_login` flag and so happened to re-read the same value. Equivalent, and
      // this says what it means.
      authHelper.setMeShared(origUserDetails);
      const restoredSession = JSON.stringify(origUserDetails);
      trustedRestoredSession = restoredSession;
      sessionStorage.setItem(CONSTANTS.LOGIN_DETAILS_KEY, restoredSession);
    };

    authHelper.logout = function (redirectUrl) {
      sessionStorage.removeItem(CONSTANTS.LOGIN_DETAILS_KEY);
      storageService.getLocalStorage()[LOGIN_PREFIX_ORIG] = '';
      storageService.getLocalStorage()[LOGIN_PREFIX] = '';
      storageService.getLocalStorage()[IMPERSONATION_KEY] = IMPERSONATION_INACTIVE;
      sessionStorage.removeItem('admin_is_shortlist_mode');
      authHelper.clearTablePreferences();

      // Django 5.x requires logout to be POST with CSRF token
      // Create a form and submit it
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = apiHelper.getApiUrl() + '/auth/logout/';

      // Add CSRF token (Django sets this as a cookie)
      // Cookie name may have a prefix (e.g., 'localhost_csrftoken')
      const csrfCookieName = (SETTINGS.COOKIE_PREFIX || '') + 'csrftoken';
      // Escape special regex characters to prevent regex injection
      const escapedCookieName = csrfCookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const csrfRegex = new RegExp(escapedCookieName + '=([^;]+)');
      const csrfToken = document.cookie.match(csrfRegex);
      if (csrfToken) {
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'csrfmiddlewaretoken';
        csrfInput.value = csrfToken[1];
        form.appendChild(csrfInput);
      } else {
        // CSRF token not found - this may happen if cookies are blocked
        // Fall back to GET-based redirect which will show login page
        console.warn('CSRF token not found, falling back to redirect');
        window.location.href = apiHelper.getApiUrl() + '/auth/login/?next=' + encodeURIComponent(redirectUrl);
        return;
      }

      // Add next parameter
      const nextUrl = apiHelper.getApiUrl() + '/auth/login/?next=' + encodeURIComponent(redirectUrl);
      const nextInput = document.createElement('input');
      nextInput.type = 'hidden';
      nextInput.name = 'next';
      nextInput.value = nextUrl;
      form.appendChild(nextInput);

      document.body.appendChild(form);
      form.submit();
    };

    /**
     * function to retrive login details either from localstorage or from server
     * if user is not logged in.
     * @returns promise with either login details or error
     */
    authHelper.getLoginDetails = function () {
      return new Promise(function (resolve, reject) {
        // Via getCachedLoginDetails, so a session that cannot belong to this sub-app falls
        // through to isLoggedIn() below instead of being served from the cache (RPR-6770). This
        // is the read that caused the payer<->admin bounce: portalReady resolves through here and
        // acts on `use_legacy_portal`, and a cached read never reaches the server to be corrected.
        const myDetails = authHelper.getCachedLoginDetails();
        if (myDetails) {
          authHelper.setMeShared(myDetails);
          return resolve(myDetails);
        }

        authHelper.isLoggedIn().then(
          function (result) {
            resolve(result.data);
          },
          reject
        );
      });
    };

    authHelper.doLogoutAll = function (addReturnUrl) {
      let returnUrl = $location.absUrl();
      if (!addReturnUrl) {
        returnUrl = $location.protocol() + '://' + $location.host();
        if ($location.port()) {
          returnUrl += (':' + $location.port() + '/#');
        }
      }
      authHelper.logout(returnUrl);
    };
  }]
);
